import { action, makeObservable, observable, reaction } from 'mobx';
import {
  KENO_RISK_TYPE,
  KENO_RISK_TYPE_NAME,
  KenoNumbers,
  SELECTED_NUMBERS_MAX_COUNT,
} from '../kenoCore/constants';
import { isMobile } from '../../../core/helpers/isMobile';
import { userSettingsStore } from '../../../store/userSettingsStore';
import { soundService } from '../../../services/baseSoundService/soundService';
import { KenoSoundsName } from '../kenoCore/constants/sounds';
import { userInfoStore } from '../../../store/userInfoStore';
import { freeBetStore } from '../../../store/freeBetStore';

class KenoBetsStore {
  isBetsClosed = false;

  selectedRiskType = KENO_RISK_TYPE.Classic;

  selectedNumbers = [];

  kenoOddsByRiskType = {};

  selectedKenoOddsByRiskType = {};

  kenoOddsBySelectedNumbersCount = {};

  winNumbers = [];

  isAutoPick = false;

  winAmount = 0;

  winOdd = 0;

  nonSelectedNumbers = KenoNumbers;

  lastResultsOdd = [];

  isRoundStart = false;

  maxWinLimit = null;

  constructor() {
    makeObservable(this, {
      winOdd: observable,
      winAmount: observable,
      winNumbers: observable,
      isAutoPick: observable,
      maxWinLimit: observable,
      isBetsClosed: observable,
      isRoundStart: observable,
      lastResultsOdd: observable,
      selectedNumbers: observable,
      selectedRiskType: observable,
      nonSelectedNumbers: observable,
      kenoOddsByRiskType: observable.deep,
      selectedKenoOddsByRiskType: observable.deep,
      kenoOddsBySelectedNumbersCount: observable.deep,
      setWinOdd: action.bound,
      autoSelect: action.bound,
      setWinAmount: action.bound,
      setWinNumbers: action.bound,
      setIsAutoPick: action.bound,
      setMaxWinLimit: action.bound,
      setIsRoundStart: action.bound,
      autoFillNumbers: action.bound,
      setIsBetsClosed: action.bound,
      updateWinNumbers: action.bound,
      setSelectedNumbers: action.bound,
      updateLastResultOdd: action.bound,
      setSelectedRiskType: action.bound,
      resetNonSelectedNumber: action.bound,
      updateSelectedNumbers: action.bound,
      setKenoOddsByRiskType: action.bound,
    });
    reaction(
      () => this.isBetsClosed,
      (value) => {
        const { setIsAllowedChangeCurrency } = userInfoStore;
        setIsAllowedChangeCurrency(!value);
        if (freeBetStore.currentFreeBet && !freeBetStore.currentFreeBet.remainingCount && !value) {
          freeBetStore.setIsShowFreeBetPopup(true);
        }
      }
    );
    reaction(
      () => this.selectedNumbers,
      (value, previousValue) => {
        if (previousValue.length !== value.length) {
          this.nonSelectedNumbers = KenoNumbers.filter((el) => !value.includes(el));
        }
      }
    );
  }

  setMaxWinLimit(limit) {
    this.maxWinLimit = limit;
  }

  updateLastResultOdd(odd) {
    const updatedLastResult = [...this.lastResultsOdd, odd];
    if (updatedLastResult.length > (isMobile() ? 3 : 5)) {
      updatedLastResult.splice(0, 1);
    }
    this.lastResultsOdd = updatedLastResult;
  }

  setIsAutoPick(value) {
    this.isAutoPick = value;
  }

  setIsRoundStart(bool) {
    this.isRoundStart = bool;
  }

  setWinOdd(odd) {
    this.winOdd = odd;
  }

  setWinAmount(winAmount) {
    this.winAmount = winAmount;
  }

  updateWinNumbers(number) {
    this.winNumbers = [...this.winNumbers, number];
  }

  setWinNumbers(winNumbers) {
    this.winNumbers = winNumbers;
  }

  setSelectedKenoOddsByRiskType(odds) {
    this.selectedKenoOddsByRiskType = odds;
  }

  setKenoOddsBySelectedNumbersCount(odds) {
    this.kenoOddsBySelectedNumbersCount = odds;
  }

  setKenoOddsByRiskType(odds) {
    this.kenoOddsByRiskType = odds;
    this.setSelectedKenoOddsByRiskType(odds[KENO_RISK_TYPE_NAME[this.selectedRiskType]]);
  }

  setSelectedRiskType(riskType) {
    if (riskType !== this.selectedRiskType) {
      soundService.play(KenoSoundsName.selection);
      this.selectedRiskType = riskType;
    }

    this.setSelectedKenoOddsByRiskType(
      this.kenoOddsByRiskType[KENO_RISK_TYPE_NAME[this.selectedRiskType]]
    );
    this.setKenoOddsBySelectedNumbersCount(
      this.selectedKenoOddsByRiskType[this.selectedNumbers.length] || {}
    );
  }

  setIsBetsClosed(bool) {
    this.isBetsClosed = bool;
  }

  updateSelectedNumbers(numbers, isAutoPick) {
    numbers.forEach((number) => {
      const includedNumber = this.selectedNumbers.includes(number);
      if (includedNumber) {
        soundService.play(KenoSoundsName.selection);
        this.selectedNumbers = this.selectedNumbers.filter((el) => el !== number);
      } else if (this.selectedNumbers.length < SELECTED_NUMBERS_MAX_COUNT) {
        !isAutoPick && soundService.play(KenoSoundsName.selection, true);
        this.selectedNumbers = [...this.selectedNumbers, number];
      }
      this.setKenoOddsBySelectedNumbersCount(
        this.selectedKenoOddsByRiskType[this.selectedNumbers.length] || {}
      );
    });
  }

  setSelectedNumbers(selectedNumbers) {
    if (this.selectedNumbers.length && !selectedNumbers.length) {
      soundService.play(KenoSoundsName.clear);
    }
    this.selectedNumbers = selectedNumbers;
    this.setKenoOddsBySelectedNumbersCount({});
  }

  resetNonSelectedNumber() {
    this.nonSelectedNumbers = KenoNumbers;
  }

  autoFillNumbers() {
    this.setIsAutoPick(true);
    new Array(SELECTED_NUMBERS_MAX_COUNT - this.selectedNumbers.length)
      .fill(null)
      .forEach((el, index, arr) => {
        setTimeout(
          () => {
            const randomIndex = Math.floor(Math.random() * this.nonSelectedNumbers.length);
            this.updateSelectedNumbers([this.nonSelectedNumbers[randomIndex]], true);
            this.nonSelectedNumbers.splice(randomIndex, 1);
            if (index === arr.length - 1) {
              this.setIsAutoPick(false);
            }
          },
          userSettingsStore.isTurboOn ? 0 : index * 80
        );
      });
  }

  autoSelect() {
    soundService.play(KenoSoundsName.selection);
    if (this.selectedNumbers.length < SELECTED_NUMBERS_MAX_COUNT) {
      this.autoFillNumbers();
    } else {
      this.resetNonSelectedNumber();
      this.selectedNumbers = [];
      this.autoFillNumbers();
    }
  }

  resetStore() {
    this.isBetsClosed = false;
    this.selectedRiskType = KENO_RISK_TYPE.Classic;
    this.selectedNumbers = [];
    this.kenoOddsByRiskType = {};
    this.selectedKenoOddsByRiskType = {};
    this.kenoOddsBySelectedNumbersCount = {};
    this.winNumbers = [];
    this.isAutoPick = false;
    this.winAmount = 0;
    this.winOdd = 0;
    this.nonSelectedNumbers = KenoNumbers;
    this.lastResultsOdd = [];
    this.isRoundStart = false;
    this.maxWinLimit = null;
  }
}

export const kenoBetsStore = new KenoBetsStore();
